import { MemberState, PermissionType } from '@assembly-web/services';
import {
  AvatarSize,
  type CriteriaItemProps,
  type CustomRule,
  type CustomRuleOption,
  type CustomRuleWithType,
} from '@assembly-web/ui';
import { produce } from 'immer';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { trackSharesheetAction } from '../../services/analytics';
import {
  generateSaveCriteriaPayloadFromRules,
  getPermissions,
  sortInitialCollectionCriteria,
} from '../../services/criteriaUtil';
import {
  CriteriaCardIconMap,
  getPermissionOptions,
  ViewerPermission,
} from '../../services/shareCollectionsData';
import type {
  ManagerCriteriaAPIResponse,
  MemberCriteriaAPIResponse,
  NonMemberCriteriaAPIResponse,
} from './queries/useGetShareCollectionCriteria';
import { useGetShareCollectionCriteria } from './queries/useGetShareCollectionCriteria';
import { useSaveCollectionsCriteriaMutation } from './queries/useSaveShareCollectionCriteria';
import { useShareCollectionsNonMemberCriteriaOptions } from './useShareCollectionsNonMemberCriteriaOptions';

enum CustomRuleType {
  Department = 'department',
  JobTitle = 'jobTitle',
  HomeLocation = 'homeLocation',
  WorkLocation = 'workLocation',
  ManagerStatus = 'manager',
  Everyone = 'everyone',
}

export enum CriteriaRuleType {
  Department = 'department',
  JobTitle = 'jobTitle',
  HomeLocation = 'homeLocation',
  WorkLocation = 'workLocation',
  ManagerStatus = 'manager',
  Everyone = 'everyone',
  Member = 'member',
  Email = 'email',
}

export enum Operator {
  Is = 'is',
  IsNot = 'isNot',
}

const operatorLabelMap: Record<string, string> = {
  [Operator.Is]: 'Is',
  [Operator.IsNot]: 'IsNot',
};

const messages = defineMessages({
  everyone: {
    defaultMessage: 'Everyone',
    id: '2XDuH4',
  },
  department: {
    defaultMessage: 'Department',
    id: 'eXcD4S',
  },
  workLocation: {
    defaultMessage: 'Work location',
    id: '4mb2dn',
  },
  manager: {
    defaultMessage: 'Manager status',
    id: 'jfRiOS',
  },
  homeLocation: {
    defaultMessage: 'Home location',
    id: 'BdIFnv',
  },
  jobTitle: {
    defaultMessage: 'Job title',
    id: 'HsQ3BK',
  },
  everyoneInThisWorkspace: {
    defaultMessage: 'Everyone in this workspace',
    id: 'zqo7Vo',
  },
  departmentRule: {
    defaultMessage: 'Department {condition} {name}',
    id: 'PR5IEt',
  },
  workLocationRule: {
    defaultMessage: 'Work location {condition} {name}',
    id: 'kUPmA/',
  },
  managerRule: {
    defaultMessage: 'Manager status {condition}',
    id: 'kKQ/Qt',
  },
  homeLocationRule: {
    defaultMessage: 'Home location {condition} {name}',
    id: 'sE0gFE',
  },
  jobTitleRule: {
    defaultMessage: 'Job title {condition} {name}',
    id: '6r41bF',
  },
  is: {
    defaultMessage: 'is',
    id: 'fXdkiI',
  },
  isNot: {
    defaultMessage: 'is not',
    id: 'alA913',
  },
  isTrue: {
    defaultMessage: 'is true',
    id: '0rzFZS',
  },
  isFalse: {
    defaultMessage: 'is false',
    id: 'thJnXm',
  },
  inviteUponSharing: {
    defaultMessage: 'Invite upon sharing',
    id: 'zTWTNM',
  },
  nonMemberCriteriaTooltipWithEveryone: {
    defaultMessage:
      'These role settings will override ‘Everyone in this workspace,’ but will not override individual role settings',
    id: 'sx3r1f',
  },
  memberCriteriaTooltip: {
    defaultMessage:
      'These manual role settings will override any roles configured using rules.',
    id: 'rTuy8M',
  },
});

const getCriteriaId = ({
  field,
  value,
  operator,
}: {
  field: string;
  value: string;
  operator: string;
}) => {
  if (field === CriteriaRuleType.Everyone) {
    return `${field}-${operator}`;
  }

  return `${field}-${operator}-${value}`;
};

export function useShareCollectionsCriteriaRules({
  rules,
  setRules,
  isPinned,
  onIsPinnedChange,
  enabled,
  collectionOwnerId,
  collectionId,
  onSaveCriteriaSuccess,
  setHasRulesChanged,
}: {
  enabled: boolean;
  collectionId: string | null;
  collectionOwnerId: string | null;
  rules: CriteriaItemProps[];
  setRules: (rules: CriteriaItemProps[]) => void;
  onSaveCriteriaSuccess: () => void;
  setHasRulesChanged: (hasRulesChanged: boolean) => void;
  onIsPinnedChange: (isPinned: boolean) => void;
  isPinned: boolean;
}) {
  const { formatMessage } = useIntl();

  const [selectedCustomRule, setSelectedCustomRule] = useState<{
    rule: CustomRule;
    type: 'searchableDropdown' | 'booleanDropdown';
  } | null>(null);
  const [initialRules, setInitialRules] = useState<CriteriaItemProps[]>([]);

  const isEveryoneRuleSelected = useMemo(
    () =>
      rules.some(
        (rule) =>
          rule.id ===
          getCriteriaId({
            value: '',
            operator: Operator.Is,
            field: CriteriaRuleType.Everyone,
          })
      ),
    [rules]
  );

  const hasNonMemberCriteriaOtherThanEveryone = useMemo(
    () =>
      rules.some(
        (rule) =>
          rule.metaData?.field !== CriteriaRuleType.Member &&
          rule.metaData?.field !== CriteriaRuleType.Email &&
          rule.metaData?.field !== CriteriaRuleType.Everyone
      ),
    [rules]
  );

  const customRules: {
    rule: CustomRule;
    type: 'searchableDropdown' | 'booleanDropdown';
  }[] = [
    {
      rule: {
        id: CustomRuleType.Department,
        label: formatMessage(messages.department),
      },
      type: 'searchableDropdown',
    },
    {
      rule: {
        id: CustomRuleType.WorkLocation,
        label: formatMessage(messages.workLocation),
      },
      type: 'searchableDropdown',
    },
    {
      rule: {
        id: CustomRuleType.HomeLocation,
        label: formatMessage(messages.homeLocation),
      },
      type: 'searchableDropdown',
    },
    {
      rule: {
        id: CustomRuleType.JobTitle,
        label: formatMessage(messages.jobTitle),
      },
      type: 'searchableDropdown',
    },
    {
      rule: {
        id: CustomRuleType.ManagerStatus,
        label: formatMessage(messages.manager),
      },
      type: 'booleanDropdown',
    },
  ];

  if (!isEveryoneRuleSelected) {
    customRules.unshift({
      rule: {
        id: CustomRuleType.Everyone,
        label: formatMessage(messages.everyone),
      },
      type: 'searchableDropdown',
    });
  }

  const { departments, jobTitles, homeLocations, workLocations } =
    useShareCollectionsNonMemberCriteriaOptions({
      enabled,
    });

  const { data: initialShareCollectionRules, isLoading } =
    useGetShareCollectionCriteria({
      id: collectionId ?? '',
      enabled,
    });

  const { mutate: saveShareCollectionCriteria, isLoading: isSaveInProgress } =
    useSaveCollectionsCriteriaMutation({
      onSuccess: onSaveCriteriaSuccess,
    });

  const getTooltipText = useCallback(
    ({
      hasEveryoneRule,
      hasNonMemberCriteriaOtherThanEveryone,
    }: {
      hasEveryoneRule?: boolean;
      hasNonMemberCriteriaOtherThanEveryone?: boolean;
    }) => {
      if (hasEveryoneRule)
        return formatMessage(messages.nonMemberCriteriaTooltipWithEveryone);

      if (hasNonMemberCriteriaOtherThanEveryone)
        return formatMessage(messages.memberCriteriaTooltip);

      return undefined;
    },
    [formatMessage]
  );

  const getEmailCriteriaCardFromAPIResponse = useCallback(
    ({
      emailRule,
      isExcluded = false,
      hasNonMemberCriteriaOtherThanEveryone,
    }:
      | {
          emailRule: Omit<MemberCriteriaAPIResponse, 'perm'>;
          isExcluded: true;
          hasNonMemberCriteriaOtherThanEveryone: boolean;
        }
      | {
          emailRule: MemberCriteriaAPIResponse;
          isExcluded: false;
          hasNonMemberCriteriaOtherThanEveryone: boolean;
        }) => ({
      id: getCriteriaId({
        value: emailRule.value,
        operator: emailRule.operator,
        field: CriteriaRuleType.Email,
      }),
      title: emailRule.meta.name,
      subtitle: emailRule.value,
      customIcon: {
        icon: CriteriaCardIconMap['pending'],
        iconBackgroundColor: 'bg-gray-4',
      },
      permission: getPermissions({
        perm: 'perm' in emailRule ? emailRule.perm : undefined,
        isExcluded,
        isOwner: false,
      }),
      permissionOptions: getPermissionOptions({
        type: CriteriaRuleType.Email,
        isOwner: false,
        formatMessage,
      }),
      metaData: {
        field: CriteriaRuleType.Email,
        operator: emailRule.operator,
        value: emailRule.value,
      },
      tooltipText: getTooltipText({
        hasNonMemberCriteriaOtherThanEveryone,
      }),
    }),
    [formatMessage, getTooltipText]
  );

  const getMemberCriteriaCardFromAPIResponse = useCallback(
    ({
      member,
      isExcluded = false,
      hasNonMemberCriteriaOtherThanEveryone,
    }:
      | {
          member: Omit<MemberCriteriaAPIResponse, 'perm'>;
          isExcluded: true;
          hasNonMemberCriteriaOtherThanEveryone: boolean;
        }
      | {
          member: MemberCriteriaAPIResponse;
          isExcluded: false;
          hasNonMemberCriteriaOtherThanEveryone: boolean;
        }) => ({
      id: getCriteriaId({
        value: member.value,
        operator: member.operator,
        field: CriteriaRuleType.Member,
      }),
      title: member.meta.name,
      subtitle: member.meta.email,
      ...(member.meta.state === MemberState.Active
        ? {
            avatar: {
              image: member.meta.image,
              name: member.meta.name,
              memberID: member.value,
              size: AvatarSize.ExtraLarge,
            },
          }
        : {
            customIcon: {
              icon: CriteriaCardIconMap[MemberState.Active.toLowerCase()],
              iconBackgroundColor:
                member.meta.state === MemberState.Pending ? 'bg-gray-4' : '',
            },
          }),
      permission: getPermissions({
        perm: 'perm' in member ? member.perm : undefined,
        isExcluded,
        isOwner: member.meta.memberId === collectionOwnerId,
      }),
      permissionOptions: getPermissionOptions({
        type: CriteriaRuleType.Member,
        isOwner: member.meta.memberId === collectionOwnerId,
        formatMessage,
      }),
      metaData: {
        field: CriteriaRuleType.Member,
        operator: member.operator,
        value: member.value,
      },
      tooltipText: getTooltipText({
        hasNonMemberCriteriaOtherThanEveryone,
      }),
    }),
    [collectionOwnerId, formatMessage, getTooltipText]
  );

  const getNonMemberCriteriaCardFromAPIResponse = useCallback(
    ({
      criteria,
      isExcluded = false,
      field,
      hasEveryoneRule,
    }:
      | {
          criteria: Omit<NonMemberCriteriaAPIResponse, 'perm'>;
          isExcluded: true;
          field: string;
          hasEveryoneRule: boolean;
        }
      | {
          criteria: NonMemberCriteriaAPIResponse;
          isExcluded: false;
          field: string;
          hasEveryoneRule: boolean;
        }): CriteriaItemProps => ({
      id: getCriteriaId({
        value: criteria.value,
        operator: criteria.operator,
        field,
      }),
      title: formatMessage(messages[`${field}Rule` as keyof typeof messages], {
        condition:
          criteria.operator === Operator.Is
            ? formatMessage(messages.is)
            : formatMessage(messages.isNot),
        name: criteria.value,
      }),
      customIcon: {
        icon: CriteriaCardIconMap[field],
      },
      permission: getPermissions({
        perm: 'perm' in criteria ? criteria.perm : undefined,
        isExcluded,
        isOwner: false,
      }),
      permissionOptions: getPermissionOptions({
        type: field as CriteriaRuleType,
        formatMessage,
      }),
      metaData: {
        field,
        operator: criteria.operator,
        value: criteria.value,
      },
      tooltipText: getTooltipText({
        hasEveryoneRule,
      }),
    }),
    [formatMessage, getTooltipText]
  );

  const getManagerCriteriaCardFromAPIResponse = useCallback(
    ({
      criteria,
      isExcluded,
      hasEveryoneRule,
    }:
      | {
          criteria: Omit<ManagerCriteriaAPIResponse, 'perm'>;
          isExcluded: true;
          hasEveryoneRule: boolean;
        }
      | {
          criteria: ManagerCriteriaAPIResponse;
          isExcluded: false;
          hasEveryoneRule: boolean;
        }): CriteriaItemProps => ({
      id: getCriteriaId({
        value: criteria.value.toString(),
        operator: criteria.operator,
        field: CriteriaRuleType.ManagerStatus,
      }),
      title: formatMessage(messages.managerRule, {
        condition: criteria.value
          ? formatMessage(messages.isTrue)
          : formatMessage(messages.isFalse),
      }),
      customIcon: {
        icon: CriteriaCardIconMap[CriteriaRuleType.ManagerStatus],
      },
      permission: getPermissions({
        perm: 'perm' in criteria ? criteria.perm : undefined,
        isExcluded,
        isOwner: false,
      }),
      permissionOptions: getPermissionOptions({
        type: CriteriaRuleType.ManagerStatus,
        formatMessage,
      }),
      metaData: {
        field: CriteriaRuleType.ManagerStatus,
        operator: criteria.operator,
        value: criteria.value.toString(),
      },
      tooltipText: getTooltipText({
        hasEveryoneRule,
      }),
    }),
    [formatMessage, getTooltipText]
  );

  useEffect(() => {
    if (initialShareCollectionRules) {
      onIsPinnedChange(initialShareCollectionRules.shouldPin);
      const hasEveryoneRule = [
        ...initialShareCollectionRules.include,
        ...initialShareCollectionRules.exclude,
      ].some((rule) => rule.field === CriteriaRuleType.Everyone);

      const hasNonMemberCriteriaOtherThanEveryone =
        initialShareCollectionRules.include.some(
          (rule) =>
            rule.field !== CriteriaRuleType.Member &&
            rule.field !== CriteriaRuleType.Email &&
            rule.field !== CriteriaRuleType.Everyone
        );

      const initialRules: CriteriaItemProps[] = [];
      sortInitialCollectionCriteria(
        initialShareCollectionRules
      ).include.forEach((includeRule) => {
        if (includeRule.field === CriteriaRuleType.Email) {
          includeRule.values.forEach((i) => {
            if (i.meta.state === MemberState.Deactivated) return;
            initialRules.push(
              getEmailCriteriaCardFromAPIResponse({
                emailRule: i,
                isExcluded: false,
                hasNonMemberCriteriaOtherThanEveryone,
              })
            );
          });
        } else if (includeRule.field === CriteriaRuleType.Member) {
          includeRule.values.forEach((i) => {
            if (i.meta.state === MemberState.Deactivated) return;
            initialRules.push(
              getMemberCriteriaCardFromAPIResponse({
                member: i,
                isExcluded: false,
                hasNonMemberCriteriaOtherThanEveryone,
              })
            );
          });
        } else if (includeRule.field === CriteriaRuleType.ManagerStatus) {
          includeRule.values.forEach((i) => {
            initialRules.push(
              getManagerCriteriaCardFromAPIResponse({
                criteria: i,
                isExcluded: false,
                hasEveryoneRule,
              })
            );
          });
        } else if (includeRule.field === CriteriaRuleType.Everyone) {
          initialRules.push({
            id: getCriteriaId({
              value: '',
              operator: includeRule.operator,
              field: CriteriaRuleType.Everyone,
            }),
            title: formatMessage(messages.everyoneInThisWorkspace),
            permission: getPermissions({
              perm: 'perm' in includeRule ? includeRule.perm : undefined,
              // 'everyone' rule cannot be excluded or be an owner
              isExcluded: false,
              isOwner: false,
            }),
            customIcon: {
              icon: CriteriaCardIconMap[includeRule.field],
            },
            permissionOptions: getPermissionOptions({
              type: includeRule.field,
              formatMessage,
            }),
            metaData: {
              field: CriteriaRuleType.Everyone,
              operator: includeRule.operator,
              value: '',
            },
          });
        } else {
          includeRule.values.forEach((i) => {
            initialRules.push(
              getNonMemberCriteriaCardFromAPIResponse({
                criteria: i,
                isExcluded: false,
                field: includeRule.field,
                hasEveryoneRule,
              })
            );
          });
        }
      });
      initialShareCollectionRules.exclude.forEach((excludeRule) => {
        if (excludeRule.field === CriteriaRuleType.Email) {
          excludeRule.values.forEach((i) => {
            initialRules.push(
              getEmailCriteriaCardFromAPIResponse({
                emailRule: i,
                isExcluded: true,
                hasNonMemberCriteriaOtherThanEveryone,
              })
            );
          });
        } else if (excludeRule.field === CriteriaRuleType.Member) {
          excludeRule.values.forEach((i) => {
            initialRules.push(
              getMemberCriteriaCardFromAPIResponse({
                member: i,
                isExcluded: true,
                hasNonMemberCriteriaOtherThanEveryone,
              })
            );
          });
        } else if (excludeRule.field === CriteriaRuleType.ManagerStatus) {
          excludeRule.values.forEach((i) => {
            initialRules.push(
              getManagerCriteriaCardFromAPIResponse({
                criteria: i,
                isExcluded: true,
                hasEveryoneRule,
              })
            );
          });
        } else {
          excludeRule.values.forEach((i) => {
            initialRules.push(
              getNonMemberCriteriaCardFromAPIResponse({
                criteria: i,
                isExcluded: true,
                field: excludeRule.field,
                hasEveryoneRule,
              })
            );
          });
        }
      });
      const owner = initialRules.find(
        (rule) => rule.permission?.id === PermissionType.Owner
      );
      const allRulesWithoutOwner = initialRules.filter(
        (rule) => rule.permission?.id !== PermissionType.Owner
      );
      const rulesToBeSet = owner
        ? [{ ...owner, tooltipText: undefined }, ...allRulesWithoutOwner]
        : initialRules;
      setInitialRules(rulesToBeSet);
      setRules(rulesToBeSet);
    }
  }, [
    collectionOwnerId,
    initialShareCollectionRules,
    formatMessage,
    getNonMemberCriteriaCardFromAPIResponse,
    setRules,
    getMemberCriteriaCardFromAPIResponse,
    getEmailCriteriaCardFromAPIResponse,
    getManagerCriteriaCardFromAPIResponse,
  ]);

  const customRuleOptions = useMemo(() => {
    if (!selectedCustomRule) return [];
    switch (selectedCustomRule.rule.id) {
      case CriteriaRuleType.Department:
        return departments;
      case CriteriaRuleType.JobTitle:
        return jobTitles;
      case CriteriaRuleType.HomeLocation:
        return homeLocations;
      case CriteriaRuleType.WorkLocation:
        return workLocations;
      default:
        return [];
    }
  }, [
    departments,
    homeLocations,
    jobTitles,
    selectedCustomRule,
    workLocations,
  ]);

  const onRulesSelectionChange = useCallback(
    (rule: CriteriaItemProps) => {
      const isAlreadySelected = rules.some(
        (selectedRule) =>
          selectedRule.id ===
          getCriteriaId({
            field: CriteriaRuleType.Member,
            operator: Operator.Is,
            value: rule.avatar?.memberID ?? rule.id,
          })
      );
      if (isAlreadySelected) {
        const element = document.getElementById(
          getCriteriaId({
            field: CriteriaRuleType.Member,
            operator: Operator.Is,
            value: rule.avatar?.memberID ?? rule.id,
          })
        );
        scrollIntoViewAndAnimateBackground(element);
        return;
      } else {
        const hasNonMemberCriteria = rules.some(
          (rule) =>
            rule.metaData?.field !== CriteriaRuleType.Member &&
            rule.metaData?.field !== CriteriaRuleType.Email
        );
        setRules(
          produce(rules, (draftRules) => {
            draftRules.unshift({
              ...rule,
              id: getCriteriaId({
                field: CriteriaRuleType.Member,
                operator: Operator.Is,
                value: rule.avatar?.memberID ?? rule.id,
              }),
              permission: ViewerPermission,
              permissionOptions: getPermissionOptions({
                type: CriteriaRuleType.Member,
                formatMessage,
              }),
              metaData: {
                field: CriteriaRuleType.Member,
                operator: Operator.Is,
                value: rule.avatar?.memberID ?? rule.id,
              },
              tooltipText: getTooltipText({
                hasNonMemberCriteriaOtherThanEveryone: hasNonMemberCriteria,
              }),
            });
          })
        );
      }
    },
    [formatMessage, getTooltipText, rules, setRules]
  );

  const onRulesPermissionChange = (
    currentRule: CriteriaItemProps,
    updatedPermission: CriteriaItemProps['permission']
  ) => {
    setRules(
      produce(rules, (draftRules) => {
        const ruleToUpdate = draftRules.find(
          (rule) => rule.id === currentRule.id
        );
        if (ruleToUpdate) {
          if (updatedPermission?.id === PermissionType.RemoveRule) {
            const isCurrentRuleOnlyNonMemberRule =
              rules.filter(
                (rule) =>
                  rule.id !== currentRule.id &&
                  rule.metaData?.field !== CriteriaRuleType.Member &&
                  rule.metaData?.field !== CriteriaRuleType.Email &&
                  rule.metaData?.field !== CriteriaRuleType.Everyone
              ).length === 0;

            trackSharesheetAction({
              actionName: 'removeMemberClicked',
              props: {
                criteriaSelected: `${currentRule.metaData?.field}${
                  currentRule.metaData?.operator &&
                  operatorLabelMap[currentRule.metaData.operator]
                }`,
                roleSelected: currentRule.permission?.id,
                ruleType: 'simple',
                isV3sharesheet: true,
                sharesheetType: 'collection',
              },
            });
            draftRules.map((rule) => {
              if (
                (ruleToUpdate.metaData?.field === CriteriaRuleType.Everyone &&
                  rule.metaData?.field !== CriteriaRuleType.Everyone &&
                  rule.metaData?.field !== CriteriaRuleType.Member &&
                  rule.metaData?.field !== CriteriaRuleType.Email) ||
                rule.permission?.id === PermissionType.Owner ||
                isCurrentRuleOnlyNonMemberRule
              ) {
                rule.tooltipText = undefined;
              }

              return rule;
            });
            draftRules.splice(draftRules.indexOf(ruleToUpdate), 1);
          } else {
            if (ruleToUpdate.metaData) {
              ruleToUpdate.id = getCriteriaId({
                field: ruleToUpdate.metaData.field,
                value: ruleToUpdate.metaData.value,
                operator: ruleToUpdate.metaData.operator,
              });
            }
            ruleToUpdate.permission = updatedPermission;
          }
        }
      })
    );
  };

  const handleOnEmailInvite = (email: string) => {
    const currentRuleId = getCriteriaId({
      value: email,
      operator: Operator.Is,
      field: CriteriaRuleType.Email,
    });
    const doesEmailRuleAlreadyExists = rules.some(
      (rule) => rule.id === currentRuleId
    );
    if (doesEmailRuleAlreadyExists) {
      const element = document.getElementById(currentRuleId);
      scrollIntoViewAndAnimateBackground(element);
      return;
    }
    trackSharesheetAction({
      actionName: 'inviteMemberClicked',
      props: {
        criteriaSelected: `${CriteriaRuleType.Email}${
          operatorLabelMap[Operator.Is]
        }`,
        roleSelected: PermissionType.Viewer,
        ruleType: 'simple',
        isV3sharesheet: true,
        sharesheetType: 'collection',
      },
    });
    setRules(
      produce(rules, (draftRules) => {
        draftRules.unshift({
          id: currentRuleId,
          title: email,
          subtitle: formatMessage(messages.inviteUponSharing),
          customIcon: {
            icon: CriteriaCardIconMap['pending'],
            iconBackgroundColor: 'bg-gray-4',
          },
          permission: ViewerPermission,
          permissionOptions: getPermissionOptions({
            type: CriteriaRuleType.Email,
            formatMessage,
          }),
          metaData: {
            field: CriteriaRuleType.Email,
            operator: Operator.Is,
            value: email,
          },
          tooltipText: getTooltipText({
            hasNonMemberCriteriaOtherThanEveryone,
          }),
        });
      })
    );
  };

  const onCustomRulesAction = ({
    selectedRules,
    ruleType,
    condition,
  }: {
    selectedRules?: CustomRuleOption[];
    ruleType: CustomRule;
    condition: string;
  }) => {
    if (ruleType.id === CustomRuleType.ManagerStatus) {
      const doesCurrentManagerStatusRuleExist = rules.some(
        (rule) =>
          rule.id ===
          getCriteriaId({
            value: ruleType.id,
            operator: condition === 'is' ? Operator.Is : Operator.IsNot,
            field: CustomRuleType.ManagerStatus,
          })
      );
      if (!doesCurrentManagerStatusRuleExist) {
        setRules(
          produce(rules, (draftRules) => {
            draftRules.unshift({
              id: getCriteriaId({
                value: condition === 'is' ? 'true' : 'false',
                operator: Operator.Is,
                field: CustomRuleType.ManagerStatus,
              }),
              title: formatMessage(messages.managerRule, {
                condition:
                  condition === 'is'
                    ? formatMessage(messages.isTrue)
                    : formatMessage(messages.isFalse),
              }),
              subtitle: undefined,
              customIcon: {
                icon: CriteriaCardIconMap[ruleType.id],
              },
              permission: ViewerPermission,
              permissionOptions: getPermissionOptions({
                type: CriteriaRuleType.ManagerStatus,
                formatMessage,
              }),
              metaData: {
                field: CustomRuleType.ManagerStatus,
                operator: Operator.Is,
                value: condition === 'is' ? 'true' : 'false',
              },
            });
          })
        );
      }
    } else if (selectedRules) {
      const hasEveryoneRule = rules.some(
        (rule) => rule.metaData?.field === CriteriaRuleType.Everyone
      );
      setRules(
        produce(rules, (draftRules) => {
          selectedRules.forEach((selectedRule) => {
            const checkIfRuleAlreadyExists = rules.some(
              (rule) =>
                rule.id ===
                getCriteriaId({
                  value: selectedRule.id,
                  operator:
                    condition === Operator.Is ? Operator.Is : Operator.IsNot,
                  field: ruleType.id,
                })
            );
            if (!checkIfRuleAlreadyExists) {
              draftRules.map((rule) => {
                if (
                  rule.metaData?.field === CriteriaRuleType.Member ||
                  rule.metaData?.field === CriteriaRuleType.Email
                ) {
                  rule.tooltipText = getTooltipText({
                    hasNonMemberCriteriaOtherThanEveryone: true,
                  });
                } else if (rule.metaData?.field !== CriteriaRuleType.Everyone) {
                  rule.tooltipText = getTooltipText({
                    hasEveryoneRule,
                  });
                }
                if (rule.permission?.id === PermissionType.Owner) {
                  rule.tooltipText = undefined;
                }
                return rule;
              });

              draftRules.unshift({
                id: getCriteriaId({
                  value: selectedRule.id,
                  operator: condition === 'is' ? Operator.Is : Operator.IsNot,
                  field: ruleType.id,
                }),
                title: formatMessage(
                  messages[`${ruleType.id}Rule` as keyof typeof messages],
                  {
                    condition:
                      condition === Operator.Is
                        ? formatMessage(messages.is)
                        : formatMessage(messages.isNot),
                    name: selectedRule.label,
                  }
                ),
                subtitle: undefined,
                customIcon: {
                  icon: CriteriaCardIconMap[ruleType.id],
                },
                permission: ViewerPermission,
                permissionOptions: getPermissionOptions({
                  type: ruleType.id as CriteriaRuleType,
                  formatMessage,
                }),
                metaData: {
                  field: ruleType.id,
                  operator: condition === 'is' ? Operator.Is : Operator.IsNot,
                  value: selectedRule.id,
                },
                tooltipText: getTooltipText({
                  hasEveryoneRule,
                }),
              });
            }
          });
        })
      );
    }
  };

  const onCustomRuleSelect = (
    selectedCustomRule: CustomRuleWithType | null
  ) => {
    if (selectedCustomRule?.rule.id === CustomRuleType.Everyone) {
      const doesEveryoneRuleAlreadyExists = rules.some(
        (rule) =>
          rule.id ===
          getCriteriaId({
            value: '',
            operator: Operator.Is,
            field: CustomRuleType.Everyone,
          })
      );
      if (!doesEveryoneRuleAlreadyExists) {
        setRules(
          produce(rules, (draftRules) => {
            draftRules.map((rule) => {
              if (rule.permission?.id === PermissionType.Owner) {
                rule.tooltipText = undefined;
              } else if (
                rule.metaData?.field === CriteriaRuleType.Member ||
                rule.metaData?.field === CriteriaRuleType.Email
              ) {
                rule.tooltipText = getTooltipText({
                  hasNonMemberCriteriaOtherThanEveryone: true,
                });
              } else if (
                rule.metaData?.field !== CriteriaRuleType.Everyone &&
                !rule.tooltipText
              ) {
                rule.tooltipText = getTooltipText({
                  hasEveryoneRule: true,
                });
              } else {
                return rule;
              }
            });
            draftRules.unshift({
              id: getCriteriaId({
                value: '',
                operator: Operator.Is,
                field: CustomRuleType.Everyone,
              }),
              title: formatMessage(messages.everyoneInThisWorkspace),
              subtitle: undefined,
              customIcon: {
                icon: CriteriaCardIconMap[CustomRuleType.Everyone],
              },
              permission: ViewerPermission,
              permissionOptions: getPermissionOptions({
                type: CriteriaRuleType.Everyone,
                formatMessage,
              }),
              metaData: {
                field: CustomRuleType.Everyone,
                operator: Operator.Is,
                value: '',
              },
            });
          })
        );
      }
    } else {
      setSelectedCustomRule(selectedCustomRule);
    }
  };

  const onDoneAction = () => {
    const payload = generateSaveCriteriaPayloadFromRules(rules, isPinned);
    saveShareCollectionCriteria({
      collectionId: collectionId ?? '',
      enabled,
      payload,
    });
    const selectedCriteria = rules.map(
      (rule) =>
        `${rule.metaData?.field}${
          rule.metaData?.operator && operatorLabelMap[rule.metaData.operator]
        }`
    );
    trackSharesheetAction({
      actionName: 'shareSettingsSaved',
      props: {
        isSoftPinnedCollection: isPinned,
        ruleType: 'simple',
        isV3sharesheet: true,
        sharesheetType: 'collection',
        criteriaSelected: uniq(selectedCriteria),
      },
    });
  };

  useEffect(() => {
    setHasRulesChanged(!isEqual(rules, initialRules));
  }, [setHasRulesChanged, rules, initialRules]);

  return {
    rules,
    isLoading,
    shouldPin: initialShareCollectionRules?.shouldPin,
    onRulesSelectionChange,
    onRulesPermissionChange,
    onCustomRulesAction,
    customRules,
    customRuleOptions,
    selectedCustomRule,
    onCustomRuleSelect,
    onDoneAction,
    handleOnEmailInvite,
    isSaveInProgress,
  };
}

function scrollIntoViewAndAnimateBackground(element: HTMLElement | null) {
  if (element) {
    element.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
    });
    element.classList.add('bg-primary-2', 'animate-pulse');

    setTimeout(() => {
      element.classList.remove('bg-primary-2', 'animate-pulse');
    }, 1000);
  }
}
